import type {
  ReportQuery,
  ReportTimePeriod,
} from "@/services/reports/reportQuery"
import type { Color } from "@/composables/options/useColorOptions"
import type { Ref, InjectionKey } from "vue"

export const GLOBAL_QUERY_KEY = Symbol(
  "globalQuery"
) as InjectionKey<Ref<ReportQuery>>

export type DefaultQuery<T> = Omit<
  T,
  keyof ReportTimePeriod
>
export type LocalQuery<T> = DefaultQuery<T> & {
  colorVariableName: Color | undefined
}
export const useReportQuery = <T extends ReportQuery>(
  defaultQuery: DefaultQuery<T>
) => {
  const globalQuery = inject(GLOBAL_QUERY_KEY)

  if (globalQuery === undefined) {
    throw new Error(
      "useReportQuery can only be used inside <ReportLayout />"
    )
  }

  const { pickColorVariableName, unpickColorVariableName } =
    useColors()

  // Really do ignore this.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const localQueries = reactive([
    {
      ...defaultQuery,
      colorVariableName: pickColorVariableName(),
    },
  ]) as LocalQuery<T>[]

  const queries = computed(() =>
    localQueries.map(
      ({
        colorVariableName: _color,
        ...query
      }: LocalQuery<T>) => ({
        ...query,
        ...globalQuery.value,
      })
    )
  )

  const addLocalQuery = () => {
    if (localQueries.length >= 10) return

    localQueries.push({
      ...defaultQuery,
      colorVariableName: pickColorVariableName(),
    })
  }

  const removeLocalQuery = (index: number) => {
    unpickColorVariableName(
      localQueries[index].colorVariableName
    )
    if (localQueries.length === 1) {
      localQueries[0] = {
        ...defaultQuery,
        colorVariableName: pickColorVariableName(),
      }
    } else {
      localQueries.splice(index, 1)
    }
  }

  const timePeriod = computed<ReportTimePeriod>(() => ({
    start: globalQuery.value.start,
    end: globalQuery.value.end,
  }))

  const setTimePeriod = (value: ReportTimePeriod) => {
    globalQuery.value = {
      ...globalQuery.value,
      ...value,
    }
  }

  return {
    localQueries,
    queries,
    addLocalQuery,
    removeLocalQuery,
    globalQuery,
    timePeriod,
    setTimePeriod,
  }
}
